import React, { FC } from 'react'
import { EditorTheme } from '../templateEditor.theme'
import { LineEnds, PlaceModeOptions, PlaceToolId, Rect, ShapeKind } from '../templateEditor.types'
import { getPlacePreviewMeta, PlacePreviewMeta } from '../elements/placementMetrics'
import { toDisplaySize } from '../templateEditor.utils'
import styles from './templateEditor__placePreview.jcss'

type PlacePreviewToolId = 'list' | 'listH' | 'table'

const isPlacePreviewTool = (type: PlaceToolId): type is PlacePreviewToolId =>
    type === 'list' || type === 'listH' || type === 'table'

type TemplateEditorPlacePreviewProps = {
    rect: Rect
    type: PlaceToolId
    theme: EditorTheme
    opts?: PlaceModeOptions
}

const shapePreviewRadius = (kind: ShapeKind, scale: number) => {
    if (kind === 'ellipse') return '50%'
    if (kind === 'rounded') return toDisplaySize(24, scale)
    return 0
}

const ShapePlacePreview: FC<{
    kind: ShapeKind
    rect: Rect
    scale: number
    fill: string
    lineEnds?: LineEnds
}> = ({ kind, rect, scale, fill, lineEnds }) => {
    if (kind === 'line') {
        const ends = lineEnds ?? { x1: 0, y1: rect.h / 2, x2: rect.w, y2: rect.h / 2 }
        const stroke = Math.max(2, Math.min(rect.w, rect.h, 4) || 4)
        return (
            <svg
                className={styles.shapeBody}
                viewBox={`0 0 ${Math.max(rect.w, 1)} ${Math.max(rect.h, 1)}`}
                preserveAspectRatio="none"
                style={{ overflow: 'visible' }}
            >
                <line
                    x1={ends.x1}
                    y1={ends.y1}
                    x2={ends.x2}
                    y2={ends.y2}
                    stroke={fill}
                    strokeWidth={stroke}
                    strokeLinecap="round"
                />
            </svg>
        )
    }

    return (
        <div
            className={styles.shapeBody}
            style={{
                background: fill,
                opacity: 0.55,
                borderRadius: shapePreviewRadius(kind, scale),
            }}
        />
    )
}

export const TemplateEditorPlacePreview: FC<TemplateEditorPlacePreviewProps> = ({ rect, type, theme, opts = {} }) => {
    const scale = theme.displayScale
    const meta: PlacePreviewMeta | null = isPlacePreviewTool(type) ? getPlacePreviewMeta(type, rect, theme) : null
    const lines: Array<{ key: string; className: string; style: React.CSSProperties }> = []
    const isShape = type === 'shape'
    const shapeKind = opts.shapeKind ?? 'rect'

    if (meta?.kind === 'list') {
        const pad = toDisplaySize(meta.pad, scale)
        const step = toDisplaySize(meta.itemStep, scale)
        const gap = toDisplaySize(meta.gap, scale)

        for (let i = 1; i < meta.count; i += 1) {
            if (meta.orientation === 'horizontal') {
                lines.push({
                    key: `v-${i}`,
                    className: styles.lineV,
                    style: { left: pad + i * step - gap / 2 },
                })
            } else {
                lines.push({
                    key: `h-${i}`,
                    className: styles.lineH,
                    style: { top: pad + i * step },
                })
            }
        }
    }

    if (meta?.kind === 'table') {
        for (let c = 1; c < meta.cols; c += 1) {
            lines.push({
                key: `v-${c}`,
                className: styles.lineV,
                style: { left: toDisplaySize(c * meta.colWidth, scale) },
            })
        }
        for (let r = 1; r < meta.rows; r += 1) {
            lines.push({
                key: `h-${r}`,
                className: styles.lineH,
                style: { top: toDisplaySize(meta.pad + r * meta.rowHeight, scale) },
            })
        }
    }

    return (
        <div
            className={isShape ? styles.shapePreview : styles.preview}
            style={{
                left: toDisplaySize(rect.x, scale),
                top: toDisplaySize(rect.y, scale),
                width: toDisplaySize(rect.w, scale),
                height: toDisplaySize(rect.h, scale),
            }}
        >
            {isShape ? (
                <ShapePlacePreview
                    kind={shapeKind}
                    rect={rect}
                    scale={scale}
                    fill={theme.demoFill}
                    lineEnds={opts.lineEnds}
                />
            ) : (
                <div className={styles.grid}>
                    {lines.map((line) => (
                        <div key={line.key} className={line.className} style={line.style} />
                    ))}
                </div>
            )}
            {meta && (
                <span key={meta.key} className={styles.badge}>
                    {meta.label}
                </span>
            )}
        </div>
    )
}
